import React, { useMemo } from 'react';
import * as PropTypes from 'prop-types';

import { formatDate } from '../../../../../utils/converters/dateConverters';
import sanitizeHtml from '../../../../../utils/sanitizeHtml';
import Popup from '../../../../common/Popup';
import { ConsentDetailsMainView } from './ConsentDetailsMainView';
import { ConsentDetailsView } from './ConsentDetailsView';
import { enhance } from './enhance';
import useConsentOutcomesInfo from '../../../../../hooks/consents/useConsentOutcomesInfo';
import useSignAppointmentConsent from '../../../../../hooks/consents/useSignAppointmentConsent';
import {
  redirectToAppointmentOverview,
  redirectToVisitDetails,
  redirectToProviders,
  redirectToPharmacy,
  redirectToRescheduleProviderDetails,
} from '../../../../../utils/appointments/redirect-utils';
import { ConsentDetailsSelfSchedulingView } from './ConsentDetailsSelfSchedulingView';

export function _ConsentDetailsPopup({
  allConsents,
  appointmentConsentsModalClose,
  appointmentConsentsModalOpen,
  currentConsent,
  intl: { formatMessage },
  isMainConsentView,
  isOpened,
  isSignConsentLoading,
  organizationName,
  providerContext,
  rescheduleFlow: { isRescheduleFlow, providerId, locationId },
  selfSchedulingFlow,
  serviceName,
  timeConsent,
  urgentVisitFlow,
  userId,
}) {
  const titlePopupProvider = formatMessage({
    id: 'appointment.consent.details.titleProvider',
  });
  const titlePopupPatient = formatMessage({
    id: 'appointment.consent.details.titlePatient',
  });
  const buttonDecline = formatMessage({
    id: 'common.decline',
  });
  const titleTypeConsent = formatMessage({
    id: 'appointment.consent.details.type',
  });
  const consentVersionTitle = formatMessage({
    id: 'appointment.consent.consentVersion',
  });

  const convertedTime = formatDate(timeConsent, formatMessage);

  const {
    buttonAccept,
    consentOutcomeTitles,
    isConsentHasMultipleOutcomes,
  } = useConsentOutcomesInfo(currentConsent);

  const handleAcceptConsent = useSignAppointmentConsent({
    consentId: currentConsent.id,
    userId,
    consentVersion: currentConsent.version,
  });

  const notSignedConsent = allConsents.filter(item => !item.accepted);

  const checkForNonSignedConsent = useMemo(
    () => (currentNonSignedConsent, nextNonSignedConsent) => {
      if (notSignedConsent.length === 0) {
        appointmentConsentsModalClose();
        if (urgentVisitFlow) {
          redirectToPharmacy();
        }
        if (selfSchedulingFlow) {
          redirectToAppointmentOverview();
        }
        return {};
      }

      if (currentNonSignedConsent.accepted) {
        appointmentConsentsModalOpen({
          organizationName,
          serviceName,
          startTime: timeConsent,
          allConsents,
          consent: nextNonSignedConsent,
          patientUserInfoId: userId,
          isMainConsentView,
        });
        return nextNonSignedConsent;
      }

      if (currentNonSignedConsent) return currentNonSignedConsent;
      return nextNonSignedConsent;
    },
    [currentConsent, notSignedConsent, urgentVisitFlow],
  );

  const { name, version, content, id, consentType } = checkForNonSignedConsent(
    currentConsent,
    notSignedConsent[0],
  );

  const subTitle = formatMessage(
    {
      id: 'appointment.consent.mainConsentTitle',
    },
    {
      name,
    },
  );

  const consentTypeName = consentType ? consentType.name : '';

  const handleClosePopup = () => {
    if (urgentVisitFlow) redirectToVisitDetails();
    if (selfSchedulingFlow) {
      if (isRescheduleFlow) {
        redirectToRescheduleProviderDetails({ locationId, providerId });
      } else {
        redirectToProviders();
      }
    }
    appointmentConsentsModalClose();
  };

  function createMarkup() {
    return {
      __html: sanitizeHtml(content),
    };
  }

  const consentViewSharedProps = useMemo(
    () => ({
      buttonAccept,
      buttonDecline,
      consentOutcomeTitles,
      createMarkup,
      handleAcceptConsent,
      handleClosePopup,
      isConsentHasMultipleOutcomes,
      isSignConsentLoading,
      name,
    }),
    [
      buttonAccept,
      buttonDecline,
      consentOutcomeTitles,
      createMarkup,
      handleAcceptConsent,
      handleClosePopup,
      isConsentHasMultipleOutcomes,
      isSignConsentLoading,
      name,
    ],
  );

  const renderView = () => {
    if (urgentVisitFlow || selfSchedulingFlow) {
      return (
        <ConsentDetailsSelfSchedulingView
          {...consentViewSharedProps}
          allConsents={allConsents}
          providerContext={providerContext}
          id={id}
          consentTypeName={consentTypeName}
          consentVersionTitle={consentVersionTitle}
          version={version}
        />
      );
    }
    if (isMainConsentView) {
      return (
        <ConsentDetailsMainView
          formatMessage={formatMessage}
          {...consentViewSharedProps}
          convertedTime={convertedTime}
          organizationName={organizationName}
          serviceName={serviceName}
          titleTypeConsent={titleTypeConsent}
        />
      );
    }
    return (
      <ConsentDetailsView
        {...consentViewSharedProps}
        allConsents={allConsents}
        consentTypeName={consentTypeName}
        consentVersionTitle={consentVersionTitle}
        convertedTime={convertedTime}
        formatMessage={formatMessage}
        id={id}
        organizationName={organizationName}
        providerContext={providerContext}
        serviceName={serviceName}
        titleTypeConsent={titleTypeConsent}
        version={version}
      />
    );
  };

  return (
    <Popup
      strictClose
      opened={isOpened}
      onClose={handleClosePopup}
      title={
        providerContext || isMainConsentView || selfSchedulingFlow
          ? titlePopupProvider
          : titlePopupPatient
      }
      subTitle={isMainConsentView && subTitle}
      mobileFullScreen={false}
      width="medium"
    >
      {renderView()}
    </Popup>
  );
}

_ConsentDetailsPopup.propTypes = {
  allConsents: PropTypes.array,
  appointmentConsentsModalClose: PropTypes.func,
  appointmentConsentsModalOpen: PropTypes.func,
  currentConsent: PropTypes.object,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  isMainConsentView: PropTypes.bool,
  isOpened: PropTypes.bool,
  isSignConsentLoading: PropTypes.bool,
  organizationName: PropTypes.string,
  providerContext: PropTypes.bool,
  rescheduleFlow: PropTypes.object,
  selfSchedulingFlow: PropTypes.bool,
  serviceName: PropTypes.string,
  timeConsent: PropTypes.string,
  urgentVisitFlow: PropTypes.bool,
  userId: PropTypes.number,
};

export const ConsentDetailsPopup = enhance(_ConsentDetailsPopup);



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/VisitConsents/ConsentDetailsPopup/index.jsx